/**
 * Configuration options for the client-side rate limiter.
 */
interface RateLimitConfig {
  windowMs: number; // Time window in milliseconds
  max: number; // Max requests per window
  backoffMs: number; // Initial backoff penalty in milliseconds
}

/**
 * Implements a client-side rate limiting mechanism with exponential backoff to prevent API abuse.
 */
export class ClientSideRateLimit {
  private requests = new Map<string, number[]>();
  private blockedUntil = new Map<string, number>();
  private blockCount = new Map<string, number>(); // To track consecutive blocks
  private config: RateLimitConfig;

  constructor(config?: Partial<RateLimitConfig>) {
    this.config = {
      windowMs: 60 * 1000, // 1 minute
      max: 10, // 10 requests per minute
      backoffMs: 5 * 1000, // 5 seconds
      ...config,
    };
  }

  isAllowed(key: string): boolean {
    const now = Date.now();

    // 1. Check if key is currently serving a penalty
    const blockedUntil = this.blockedUntil.get(key);
    if (blockedUntil && now < blockedUntil) {
      return false;
    }

    // 2. Clean up old requests from the time window
    const requests = this.requests.get(key) || [];
    const validRequests = requests.filter(
      time => now - time < this.config.windowMs
    );

    // 3. Check if the request count exceeds the maximum
    if (validRequests.length >= this.config.max) {
      // Increment block counter and apply exponential backoff
      const currentBlockCount = this.blockCount.get(key) || 0;
      const backoffFactor = Math.pow(2, currentBlockCount);
      this.blockedUntil.set(key, now + this.config.backoffMs * backoffFactor);
      this.blockCount.set(key, currentBlockCount + 1);
      // Keep the valid (but over-limit) requests so the user remains blocked until the window clears
      this.requests.set(key, validRequests);
      return false;
    }

    // 4. If request is allowed, record it and reset block counter
    this.blockCount.delete(key); // Reset penalty counter on a successful request
    validRequests.push(now);
    this.requests.set(key, validRequests);

    return true;
  }

  reset(key: string): void {
    this.requests.delete(key);
    this.blockedUntil.delete(key);
    this.blockCount.delete(key);
  }
}
